import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Smile, User, Phone, Calendar, Check, ChevronRight, 
  ArrowLeft, Clock, CheckCircle2, AlertCircle, Home
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kiosk Steps
const STEPS = {
  WELCOME: 'welcome',
  SEARCH: 'search',
  CONFIRM: 'confirm',
  FORM: 'form',
  COMPLETE: 'complete'
};

const KioskMode = () => {
  const [step, setStep] = useState(STEPS.WELCOME);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    emergency_contact: '',
    current_medications: '',
    allergies: '',
    health_changes: ''
  });

  // Auto-reset after inactivity
  useEffect(() => {
    let timeout;
    const resetKiosk = () => {
      setStep(STEPS.WELCOME);
      setSearchTerm('');
      setSearchResults([]);
      setSelectedPatient(null);
      setSelectedAppointment(null);
      setTodayAppointments([]);
      setFormData({
        emergency_contact: '',
        current_medications: '',
        allergies: '',
        health_changes: ''
      });
    };

    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(resetKiosk, 120000); // Reset after 2 minutes of inactivity
    };

    window.addEventListener('click', resetTimer);
    window.addEventListener('touchstart', resetTimer);
    window.addEventListener('keypress', resetTimer);
    resetTimer();

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
      window.removeEventListener('keypress', resetTimer);
    };
  }, []);

  const searchPatient = async () => {
    if (searchTerm.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }

    setLoading(true);
    try {
      // Using public kiosk endpoint (or we'll handle auth differently)
      const response = await axios.get(`${API_URL}/api/kiosk/search?q=${encodeURIComponent(searchTerm)}`);
      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast.info('No patients found. Please check at the front desk.');
      }
    } catch (error) {
      // Fallback: Try searching without auth for demo
      try {
        const response = await axios.get(`${API_URL}/api/patients?search=${encodeURIComponent(searchTerm)}`);
        setSearchResults(response.data.slice(0, 5));
      } catch {
        toast.error('Unable to search. Please check at the front desk.');
        setSearchResults([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectPatient = async (patient) => {
    setSelectedPatient(patient);
    setLoading(true);
    
    try {
      // Fetch today's appointments for this patient
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(
        `${API_URL}/api/dental/appointments?date=${today}&patient_id=${patient.id}`
      ).catch(() => ({ data: [] }));
      
      setTodayAppointments(response.data);
      setStep(STEPS.CONFIRM);
    } catch (error) {
      setTodayAppointments([]);
      setStep(STEPS.CONFIRM);
    } finally {
      setLoading(false);
    }
  };

  const confirmAppointment = async (appointment) => {
    setSelectedAppointment(appointment);
    setStep(STEPS.FORM);
  };

  const completeCheckIn = async () => {
    setLoading(true);
    try {
      // Update appointment status to checked_in
      if (selectedAppointment) {
        await axios.put(
          `${API_URL}/api/dental/appointments/${selectedAppointment.id}?status=checked_in`,
          {}
        ).catch(() => {});
      }

      // Save health form data (in production, this would save to patient record)
      console.log('Check-in data:', { patient: selectedPatient, form: formData });
      
      setStep(STEPS.COMPLETE);
      
      // Auto-reset after 10 seconds
      setTimeout(() => {
        setStep(STEPS.WELCOME);
        setSearchTerm('');
        setSearchResults([]);
        setSelectedPatient(null);
        setSelectedAppointment(null);
        setTodayAppointments([]);
        setFormData({
          emergency_contact: '',
          current_medications: '',
          allergies: '',
          health_changes: ''
        });
      }, 10000);
      
    } catch (error) {
      toast.error('Check-in failed. Please see front desk.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    switch (step) {
      case STEPS.SEARCH:
        setStep(STEPS.WELCOME);
        break;
      case STEPS.CONFIRM:
        setStep(STEPS.SEARCH);
        setSelectedPatient(null);
        break;
      case STEPS.FORM:
        setStep(STEPS.CONFIRM);
        setSelectedAppointment(null);
        break;
      default:
        setStep(STEPS.WELCOME);
    }
  };

  // Welcome Screen
  const WelcomeScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
      <div className="w-24 h-24 rounded-full bg-teal-100 flex items-center justify-center mb-8">
        <Smile className="w-14 h-14 text-teal-600" />
      </div>
      <h1 className="text-5xl font-bold text-slate-800 mb-4">Welcome!</h1>
      <p className="text-2xl text-slate-600 mb-12">Tap below to check in for your appointment</p>
      <Button 
        size="lg" 
        className="text-2xl px-12 py-8 bg-teal-600 hover:bg-teal-700 rounded-xl shadow-lg"
        onClick={() => setStep(STEPS.SEARCH)}
      >
        <User className="w-8 h-8 mr-4" />
        Check In
      </Button>
      <p className="text-slate-400 mt-8">Touch anywhere to begin</p>
    </div>
  );

  // Search Screen
  const SearchScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-8">
      <Button 
        variant="ghost" 
        className="absolute top-8 left-8 text-slate-600"
        onClick={goBack}
      >
        <ArrowLeft className="w-6 h-6 mr-2" />
        Back
      </Button>

      <h1 className="text-4xl font-bold text-slate-800 mb-8">Find Your Name</h1>
      
      <div className="w-full max-w-xl space-y-6">
        <div className="flex gap-4">
          <Input
            type="text"
            placeholder="Enter your last name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="text-2xl py-6 px-6"
            onKeyDown={(e) => e.key === 'Enter' && searchPatient()}
            autoFocus
          />
          <Button 
            size="lg" 
            className="px-8 bg-teal-600 hover:bg-teal-700"
            onClick={searchPatient}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </div>

        {searchResults.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {searchResults.map(patient => (
              <div
                key={patient.id}
                className="p-6 border-b last:border-b-0 cursor-pointer hover:bg-teal-50 transition-colors flex justify-between items-center"
                onClick={() => selectPatient(patient)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-xl font-medium">
                    {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                  </div>
                  <div>
                    <div className="text-xl font-medium">{patient.last_name}, {patient.first_name}</div>
                    <div className="text-slate-500">DOB: {patient.date_of_birth}</div>
                  </div>
                </div>
                <ChevronRight className="w-8 h-8 text-slate-400" />
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-slate-500 text-lg mt-8">
          Can't find your name? Please check in at the front desk.
        </p>
      </div>
    </div>
  );

  // Confirm Screen
  const ConfirmScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-8">
      <Button 
        variant="ghost" 
        className="absolute top-8 left-8 text-slate-600"
        onClick={goBack}
      >
        <ArrowLeft className="w-6 h-6 mr-2" />
        Back
      </Button>

      <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-6">
        <User className="w-8 h-8 text-teal-600" />
      </div>

      <h1 className="text-3xl font-bold text-slate-800 mb-2">
        {selectedPatient?.first_name} {selectedPatient?.last_name}
      </h1>
      <p className="text-xl text-slate-500 mb-8">Is this you?</p>

      {todayAppointments.length > 0 ? (
        <div className="w-full max-w-xl">
          <h2 className="text-xl font-medium text-slate-700 mb-4">Your appointment(s) today:</h2>
          <div className="space-y-4 mb-8">
            {todayAppointments.map(appt => (
              <div
                key={appt.id}
                className="bg-white rounded-xl shadow p-6 cursor-pointer hover:ring-2 hover:ring-teal-500 transition-all"
                onClick={() => confirmAppointment(appt)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-teal-600">{appt.scheduled_time}</div>
                    <div>
                      <div className="font-medium text-lg">{appt.services?.[0]?.service_name || 'Dental Appointment'}</div>
                      <div className="text-slate-500">with {appt.provider_name}</div>
                    </div>
                  </div>
                  <ChevronRight className="w-8 h-8 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center mb-8">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <p className="text-xl text-slate-600">No appointments found for today.</p>
          <p className="text-slate-500">Please check with the front desk.</p>
        </div>
      )}

      <div className="flex gap-4">
        <Button variant="outline" size="lg" className="text-lg px-8" onClick={goBack}>
          Not Me
        </Button>
        {todayAppointments.length === 0 && (
          <Button size="lg" className="text-lg px-8 bg-teal-600 hover:bg-teal-700" onClick={() => setStep(STEPS.FORM)}>
            Continue Anyway
          </Button>
        )}
      </div>
    </div>
  );

  // Health Form Screen
  const FormScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-8">
      <Button 
        variant="ghost" 
        className="absolute top-8 left-8 text-slate-600"
        onClick={goBack}
      >
        <ArrowLeft className="w-6 h-6 mr-2" />
        Back
      </Button>

      <h1 className="text-3xl font-bold text-slate-800 mb-2">Quick Health Update</h1>
      <p className="text-slate-500 mb-8">Please answer a few questions</p>

      <div className="w-full max-w-xl space-y-6">
        <div>
          <Label className="text-lg">Any changes to your health since last visit?</Label>
          <textarea
            className="w-full mt-2 p-4 text-lg border rounded-xl resize-none"
            rows={2}
            placeholder="None / Describe changes..."
            value={formData.health_changes}
            onChange={(e) => setFormData({...formData, health_changes: e.target.value})}
          />
        </div>

        <div>
          <Label className="text-lg">Current medications?</Label>
          <textarea
            className="w-full mt-2 p-4 text-lg border rounded-xl resize-none"
            rows={2}
            placeholder="None / List medications..."
            value={formData.current_medications}
            onChange={(e) => setFormData({...formData, current_medications: e.target.value})}
          />
        </div>

        <div>
          <Label className="text-lg">Any allergies?</Label>
          <textarea
            className="w-full mt-2 p-4 text-lg border rounded-xl resize-none"
            rows={2}
            placeholder="None / List allergies..."
            value={formData.allergies}
            onChange={(e) => setFormData({...formData, allergies: e.target.value})}
          />
        </div>

        <Button 
          size="lg" 
          className="w-full text-xl py-6 bg-teal-600 hover:bg-teal-700 mt-8"
          onClick={completeCheckIn}
          disabled={loading}
        >
          {loading ? 'Processing...' : (
            <>
              <Check className="w-6 h-6 mr-3" />
              Complete Check-In
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Complete Screen
  const CompleteScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-teal-50">
      <div className="w-24 h-24 rounded-full bg-teal-500 flex items-center justify-center mb-8 animate-bounce">
        <CheckCircle2 className="w-14 h-14 text-white" />
      </div>
      <h1 className="text-5xl font-bold text-teal-700 mb-4">You're Checked In!</h1>
      <p className="text-2xl text-slate-600 mb-4">
        Thank you, {selectedPatient?.first_name}
      </p>
      {selectedAppointment && (
        <p className="text-xl text-slate-500 mb-8">
          Your appointment is at {selectedAppointment.scheduled_time}
        </p>
      )}
      <p className="text-lg text-slate-500">Please have a seat. We'll call you shortly.</p>
      <div className="mt-12 text-slate-400">
        <Clock className="w-6 h-6 inline mr-2" />
        This screen will reset automatically
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200" data-testid="kiosk-mode">
      <Toaster position="top-center" />
      
      {/* Home button always visible except on welcome */}
      {step !== STEPS.WELCOME && step !== STEPS.COMPLETE && (
        <Button 
          variant="ghost" 
          className="absolute top-8 right-8 text-slate-600"
          onClick={() => setStep(STEPS.WELCOME)}
        >
          <Home className="w-6 h-6 mr-2" />
          Start Over
        </Button>
      )}

      {step === STEPS.WELCOME && <WelcomeScreen />}
      {step === STEPS.SEARCH && <SearchScreen />}
      {step === STEPS.CONFIRM && <ConfirmScreen />}
      {step === STEPS.FORM && <FormScreen />}
      {step === STEPS.COMPLETE && <CompleteScreen />}
    </div>
  );
};

export default KioskMode;
